import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, BarChart3, Calculator, Loader2, Truck } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { errorMessage } from "../lib/api";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const CURRENCIES = ["EUR", "USD", "GBP", "PLN", "RON"];
const COUNTRIES = [
  ["HR", "Croatia"],
  ["SI", "Slovenia"],
  ["AT", "Austria"],
  ["DE", "Germany"],
  ["IT", "Italy"],
  ["PL", "Poland"],
  ["RO", "Romania"],
  ["NL", "Netherlands"],
  ["IE", "Ireland"],
  ["OT", "Other"],
];

const Signup = () => {
  const { t } = useI18n();
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    company_name: "",
    country: "HR",
    currency: "EUR",
    tax_id: "",
    admin_name: "",
    email: "",
    password: "",
    admin_sub_type: "fleet_owner",
  });

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await signup(form);
      toast.success(t("auth.companyCreated"));
      navigate("/app/scan", { replace: true });
    } catch (err) {
      toast.error(errorMessage(err, "Could not create the account"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="mx-auto flex max-w-[1000px] items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <BarChart3 className="h-4 w-4" />
            </span>
            <span className="font-display text-base font-semibold">LedgerLens</span>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">
              {t("auth.alreadyHave")}
            </Link>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[560px] px-4 py-10 sm:px-6">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <span className={step === 1 ? "text-primary" : ""}>{t("auth.signupStep1")}</span>
          <span className="h-px w-6 bg-border" />
          <span className={step === 2 ? "text-primary" : ""}>{t("auth.signupStep2")}</span>
        </div>
        <h1 className="mt-3 font-display text-2xl font-semibold">
          {step === 1 ? t("auth.signupTitle1") : t("auth.signupTitle2")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("auth.signupSub")}
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          {step === 1 ? (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="company_name">{t("auth.companyName")}</Label>
                <Input
                  id="company_name"
                  required
                  placeholder="Transport Novak d.o.o."
                  value={form.company_name}
                  onChange={set("company_name")}
                  data-testid="signup-company-input"
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>{t("auth.country")}</Label>
                  <Select
                    value={form.country}
                    onValueChange={(v) => setForm((p) => ({ ...p, country: v }))}
                  >
                    <SelectTrigger data-testid="signup-country-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map(([code, label]) => (
                        <SelectItem key={code} value={code}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("common.currency")}</Label>
                  <Select
                    value={form.currency}
                    onValueChange={(v) => setForm((p) => ({ ...p, currency: v }))}
                  >
                    <SelectTrigger data-testid="signup-currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="tax_id">{t("auth.taxId")}</Label>
                <Input id="tax_id" value={form.tax_id} onChange={set("tax_id")} data-testid="signup-taxid-input" />
              </div>
              <div className="space-y-2 pt-1">
                <Label>{t("auth.orgTypeLabel")}</Label>
                <p className="text-xs text-muted-foreground">{t("auth.orgTypeHint")}</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {[
                    { key: "fleet_owner", icon: Truck },
                    { key: "accounting_firm", icon: Calculator },
                  ].map(({ key, icon: Icon }) => {
                    const active = form.admin_sub_type === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setForm((p) => ({ ...p, admin_sub_type: key }))}
                        aria-pressed={active}
                        className={`rounded-xl border p-3 text-left transition-colors duration-150 ${
                          active
                            ? "border-primary bg-primary/5 ring-1 ring-primary"
                            : "hover:bg-accent focus-visible:bg-accent"
                        }`}
                        data-testid={`signup-subtype-${key}`}
                      >
                        <span className="flex items-center gap-2 text-sm font-medium">
                          <Icon className="h-4 w-4 text-primary" />
                          {t(`auth.orgType_${key}`)}
                        </span>
                        <span className="mt-1 block text-xs text-muted-foreground">
                          {t(`auth.orgType_${key}_hint`)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <Button
                type="button"
                className="w-full"
                disabled={!form.company_name.trim() || !form.admin_sub_type}
                onClick={() => setStep(2)}
                data-testid="signup-next-button"
              >
                {t("common.continue")} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="admin_name">{t("auth.yourName")}</Label>
                <Input
                  id="admin_name"
                  required
                  value={form.admin_name}
                  onChange={set("admin_name")}
                  data-testid="signup-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email">{t("auth.workEmail")}</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  data-testid="signup-email-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">{t("auth.passwordHint")}</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  minLength={6}
                  value={form.password}
                  onChange={set("password")}
                  data-testid="signup-password-input"
                />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setStep(1)}>
                  {t("common.back")}
                </Button>
                <Button type="submit" className="flex-1" disabled={busy} data-testid="signup-submit-button">
                  {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} {t("auth.createCompany")}
                </Button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default Signup;
